// L'API de minibreek (api/, FastAPI) : les projets (des « affaires » côté API), leur analyse et leur mémoire, et la
// base de l'entreprise, tels que Kanon les rend.

export const API = process.env.NEXT_PUBLIC_API_URL ?? "http://127.0.0.1:8100";
// Le bac à sable des vues MCP Apps, servi par l'API sur une autre origine que l'interface. En local, l'API en est
// déjà une ; derrière un même hôte (lab), il lui faut le sien.
export const APPS = process.env.NEXT_PUBLIC_APPS_URL ?? API;

// Annoncé quand l'API refuse une requête faute de session (échue, ou compte retiré) : components/Entree redemande le
// mot de passe.
export const SESSION = "minibreek:session";

// ChatKit vérifie le domaine qui l'héberge ; en local, la clé de développement suffit.
export const DOMAIN_KEY =
  process.env.NEXT_PUBLIC_CHATKIT_DOMAIN_KEY ?? "domain_pk_localhost_dev";

// Là où travaille un agent : un projet (son DCE en lecture, son analyse et son mémoire en écriture), ou l'entreprise
// (sa base en écriture, les projets en cours en lecture).
export type Portee = { genre: "projet"; id: string } | { genre: "entreprise" };
export const ENTREPRISE: Portee = { genre: "entreprise" };

// Le fil ouvert d'une portée, retenu d'une visite à l'autre. La clé d'un projet n'a pas changé : les fils retenus
// avant l'accueil de l'entreprise se rouvrent.
export const cleFil = (portee: Portee) =>
  portee.genre === "projet"
    ? `minibreek.fil.${portee.id}`
    : "minibreek.fil-entreprise";

// Les largeurs des colonnes de l'espace de travail : un réglage du navigateur, pas de l'affaire.
export const CLE_LARGEURS = "minibreek.largeurs";

export type Diagnostic = {
  file: string;
  line: number | null;
  col: number | null;
  level: "error" | "warning";
  message: string;
};

export class ErreurApi extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public diagnostics: Diagnostic[] = [],
  ) {
    super(message);
  }
}

export type Statut = "a-ecrire" | "brouillon" | "a-relire" | "valide";
export const STATUTS: Statut[] = [
  "a-ecrire",
  "brouillon",
  "a-relire",
  "valide",
];
export const LIBELLES_STATUT: Record<Statut, string> = {
  "a-ecrire": "À écrire",
  brouillon: "Brouillon",
  "a-relire": "À relire",
  valide: "Validé",
};

export type AffaireResume = {
  id: string;
  objet: string;
  nom_court: string | null; // « sujet @ lieu », choisi par l'analyse ; null avant elle
  reference: string;
  acheteur: string;
  lieu: string;
  procedure: string;
  date_limite: string;
  pieces: number;
  analyse: boolean;
  memoire: Partial<Record<Statut, number>> | null;
  a_trancher: number; // les arbitrages en attente d'une décision
  a_faire: number; // les tâches que l'entreprise doit accomplir
  // les jalons de la pipeline, et ses grandes étapes pour la frise
  avancement: {
    faits: number;
    total: number;
    courant: string | null;
    etapes: GrandeEtape[];
    // le travail en cours, de la grande étape à la plus fine : l'étape, l'activité, la sous-activité
    en_cours?: { nom: string; acteurs: string[] }[];
    decision: Decision | null; // la décision go / no-go de la direction, pas encore prise : null
    issue?: Issue | null; // après un go, ce que devient l'offre ; null tant qu'elle est en production
  };
};

// La décision go / no-go : la direction la prend d'un bouton, la grille l'éclaire sans la prendre.
export type Decision = "go" | "no-go";

// Après un go, l'issue de l'offre : remise à l'acheteur, puis gagnée ou perdue (le kanban des projets).
export type Issue = "remise" | "gagne" | "perdu";

export type Piece = {
  id: string;
  nom: string;
  dossier: string;
  type: string;
  lot_id: string;
  pages: number | null;
  fichier: string | null;
  texte: boolean;
};

export type Affaire = {
  id: string;
  objet: string;
  nom_court: string | null; // « sujet @ lieu », choisi par l'analyse ; null avant elle
  reference: string;
  acheteur: string;
  maitre_oeuvre: string;
  lieu: string;
  procedure: string;
  date_limite: string;
  validite_offres: string;
  delai: string;
  resume: string;
  lots: { id: string; numero: number; nom: string }[];
  pieces: Piece[];
  analyse: boolean;
  memoire: boolean;
};

export type Analyse = {
  id: string;
  titre: string;
  synthese: string;
  criteres: {
    id: string;
    nom: string;
    categorie: string;
    points: number;
    source: string;
  }[];
  jalons: {
    id: string;
    nom: string;
    type: string;
    date: string;
    imperatif: boolean;
  }[];
  pieces_a_remettre: {
    id: string;
    nom: string;
    partie: string;
    signature: boolean;
  }[];
  points_d_attention: {
    id: string;
    constat: string;
    gravite: string;
    source: string;
  }[];
  exigences: { id: string; nom: string; critere_id: string; source: string }[];
  source: string;
  version: string;
};

export type Chapitre = {
  id: string;
  titre: string;
  statut: Statut;
  critere: { id: string; nom: string; points: number | null } | null;
  parent: string | null;
  niveau: number;
  texte: string;
  bloc: string;
  ligne: number;
};

export type Memoire = {
  id: string;
  titre: string;
  candidat: string;
  lots: string;
  chapitres: Chapitre[];
  source: string;
  version: string;
  avertissements: Diagnostic[];
};

// L'échelle de puissance du chat (api/app/config.py, NIVEAUX), du plus rapide au plus fort.
export type Niveau = { id: string; libelle: string; description: string };

export type Config = {
  niveau: string; // celui d'un fil neuf
  niveaux: Niveau[];
  sandbox: "e2b" | "local";
  cles: {
    e2b: boolean;
    anthropic: boolean;
    openai: boolean;
    openrouter: boolean;
  };
};

// Un fil est un agent : son titre devient, après sa première réponse, un nom de trois mots au plus (« Auditeur de
// projets »). `maj` change quand un autre agent lui écrit ; `occupe` dit qu'il travaille.
export type Fil = {
  id: string;
  titre: string;
  niveau: string;
  creee: string;
  maj: string;
  occupe: boolean;
  affaire: string | null;
  reponses: number; // les réponses de l'agent : celles qu'on n'a pas lues se comptent dans la liste des fils
  recap?: string | null; // son dernier récap (« > **Récap** : … ») : où il en est, pour qui revient
};

// La base de l'entreprise : des rubriques groupées, chacune faite de fiches. Une fiche est un bloc du document Kanon
// de sa rubrique ; elle s'édite comme un chapitre du mémoire.
export type Champ = { cle: string; libelle: string; valeur: string | null }; // null : champ prévu, pas rempli

export type Fiche = {
  id: string;
  rubrique: string;
  titre: string;
  categorie: string | null;
  tags: string[];
  champs: Champ[];
  texte: string;
  document: string;
  bloc: string;
  version: string;
};

export type Rubrique = {
  id: string;
  titre: string;
  groupe: string;
  document: string;
  champs: { cle: string; libelle: string }[];
  modele: string | null; // le bloc d'une fiche neuve ; vide si la rubrique n'en prend pas (l'identité)
  version: string; // la version du document de la rubrique
  fiches: Fiche[];
};

export type Entreprise = {
  nom: string | null;
  version: string;
  rubriques: Rubrique[];
};

export type Document = "analyse" | "memoire";

// Les arbitrages d'un projet : les décisions que l'agent ne prend pas seul (choix des lots, trame du mémoire, point
// ambigu du DCE…). Il les pose ; l'entreprise tranche, parmi les options ou par un « autre » libre, qui devient une
// option. Le choix est l'id d'une option : reformuler l'option la garde choisie.
export type StatutArbitrage = "a-trancher" | "tranche" | "ecarte";
export type OptionArbitrage = { id: string; texte: string };
export type Arbitrage = {
  id: string;
  titre: string;
  contexte: string; // markdown
  options: OptionArbitrage[];
  choix: string | null; // l'id de l'option retenue, quand il est tranché
  precision: string | null; // ce que l'entreprise ajoute à son choix, ou pourquoi elle l'écarte
  statut: StatutArbitrage;
  gravite: "info" | "attention" | "critique";
  lot: string | null; // l'id du lot, ou null pour tout le marché
  source: string; // les articles du DCE
  pose: string; // la date où l'agent l'a posé (ISO)
};
export type Arbitrages = { arbitrages: Arbitrage[]; version: string };
// Trancher : une option par son id, ou un autre choix ; la précision accompagne le choix (ou l'écart).
export type ChoixArbitrage = {
  choix?: string | null;
  autre?: string | null;
  precision?: string | null;
};

// Les tâches d'un projet : ce qui manque et que l'agent ne peut pas produire (un CV, une attestation, une visite…).
// Il les pose ; l'entreprise les fait, ou les abandonne.
export type StatutTache = "a-faire" | "fait" | "abandonne";
export type Tache = {
  id: string;
  titre: string;
  contexte: string; // markdown
  statut: StatutTache;
  pour: string | null; // qui doit la faire
  etape: string | null; // l'étape de la pipeline qui l'attend
  etape_nom: string | null;
  source: string | null;
  pose: string;
};
export type Taches = { taches: Tache[]; version: string };

// L'orchestrateur (app/objectif.py) : il mène chaque projet suivi jusqu'au mémoire prêt, mission par mission, et ne
// s'arrête que si c'est prêt ou si un point attend l'entreprise.
export type EtatObjectif = {
  affaire: string;
  objet: string;
  mission: { id: string; nom: string } | null; // la prochaine, ou celle en cours
  manque: string[]; // les jalons de la mission pas encore atteints
  arbitrages: { id: string; titre: string; gravite: Arbitrage["gravite"] }[];
  taches: {
    id: string;
    titre: string;
    pour: string | null;
    etape: string | null;
    pose: string;
  }[];
  a_valider: number; // les chapitres du mémoire pas encore validés
  fil: string | null; // le fil de l'agent du projet, où il confie les missions
  raison: string | null; // pourquoi il est bloqué
  // la décision go / no-go : a-prendre quand la grille est remplie et que la suite l'attend
  decision: Decision | "a-prendre" | null;
  etat: "pret" | "en-cours" | "bloque" | "a-lancer" | "en-pause" | "no-go";
};

// L'agent du projet : un fil toujours là, épinglé en tête des chats du projet ; l'orchestrateur lui confie les
// missions, et « Demander à l'agent » lui écrit.
export type AgentDuProjet = EtatObjectif & {
  nom: string;
  fil: string;
  occupe: boolean; // un tour en cours dans son fil
};

// L'orchestrateur lui-même : un agent de l'entreprise toujours là, qui se réveille à intervalles réguliers.
export type Orchestrateur = {
  nom: string;
  fil: string; // son fil, épinglé en tête des agents
  etat: "veille" | "au-travail" | "en-pause";
  actif: boolean;
  intervalle: number; // secondes entre deux réveils (0 : jamais)
  dernier_reveil: string | null; // ISO, heure locale de l'API
  prochain_reveil: string | null;
  au_travail: {
    affaire: string;
    objet: string;
    mission: string;
    fil: string;
  }[];
  pauses: string[]; // les projets qu'il laisse
};

// La Veille (app/veille.py) : le second agent de l'entreprise toujours là, qui surveille les appels d'offres de la
// Marketplace. Elle ne tourne que quand on lui parle.
export type Veille = {
  nom: string;
  fil: string; // son fil, épinglé sous celui de l'orchestrateur
  ouvertes: number; // les annonces dont la date limite n'est pas passée
  fortes: number; // parmi elles, celles dont le match atteint `seuil`
  seuil: number;
  occupe: boolean;
};

// L'Intendant (app/intendant.py) : le troisième agent de l'entreprise toujours là, qui tient la base de l'entreprise (le
// Socle). Il ne tourne que quand on lui parle.
export type Intendant = {
  nom: string;
  fil: string; // son fil, épinglé sous celui du Veilleur
  fiches: number; // les fiches de la base de l'entreprise
  documents: number; // ses documents présents, sur les six
  occupe: boolean;
};

// La pipeline d'un projet : la réponse à l'appel d'offres, des processus et des jalons imbriqués (le modèle vient de
// la pipeline de Breek). Un jalon est atteint à la main, ou de lui-même quand les documents le montrent (l'analyse
// existe, tous les chapitres sont validés…). L'état d'un processus se déduit de ses jalons.
export type EtatEtape = "fait" | "en-cours" | "a-venir";

// Une grande étape : un processus de premier niveau et son jalon de sortie (voir grandesEtapes).
export type GrandeEtape = {
  id: string; // l'étape de tête, où la ligne défile
  nom: string;
  etat: EtatEtape;
  faits: number;
  total: number;
  jalons?: { nom: string; fait: boolean }[]; // ses jalons, dans l'ordre (le résumé d'un projet les donne)
};
export type EtapePipeline = {
  id: string;
  genre: "processus" | "jalon";
  nom: string;
  acteurs: string[]; // ids des acteurs : entreprise, acheteur, agent…
  description: string;
  etat: EtatEtape;
  atteint: { date: string; auto: boolean } | null; // pour un jalon
  enfants: EtapePipeline[];
};
export type Acteur = { id: string; nom: string; couleur: string };
export type GoNoGo = {
  criteres: { id: string; nom: string; note: number | null }[]; // note de 1 (défavorable) à 5 (favorable)
  total: number;
  max: number;
  notes: number; // les critères notés
  decision: Decision | null; // celle de la direction
  decide: string | null; // sa date
};
export type Avancement = {
  acteurs: Acteur[];
  etapes: EtapePipeline[];
  faits: number;
  total: number;
  courant: string | null; // le premier jalon pas encore atteint
  go_no_go: GoNoGo;
  issue: Issue | null;
  version: string;
};

// Le dossier d'un projet, comme un drive à quatre racines : la candidature (ce qui part avec l'offre), le privé (ce qui
// reste à l'entreprise), et deux sources en lecture seule, le DCE de l'acheteur et le Socle de l'entreprise. Les
// chemins commencent par le nom de leur racine. Le mémoire technique fait partie de la candidature sans être un fichier
// du dossier : l'API en donne l'avancement.
export type RacineDossier = "candidature" | "private" | "dce" | "socle";
export type EntreeDossier = {
  chemin: string;
  nom: string;
  genre: "dossier" | "fichier";
  taille: number | null;
  maj: string; // ISO
  lecture_seule: boolean;
  piece?: string; // un fichier du DCE : sa pièce
  fiche?: string; // un fichier du Socle : sa fiche
  ref?: string; // un fichier du DCE ou du Socle : sa référence dans la base (lib/references)
  type?: string | null; // un fichier .kan : le `type:` de son frontmatter (Arbitrages, Avancement…), null sans
};
export type AvancementMemoire = {
  titre: string;
  chapitres: number;
  statuts: Record<Statut, number>;
  pourcentage: number; // pondéré : brouillon ½, à relire ¾, validé 1
  maj: string;
};
// Une racine d'un espace de fichiers, dans l'ordre d'affichage : celles d'un projet (RacineDossier), ou celles de
// l'organisation (notes, entreprise, projets, kanon, marketplace). Son nom manque dans les réponses d'avant.
export type Racine = { id: string; nom?: string; lecture_seule: boolean };
export type Dossier = {
  racines: Racine[];
  entrees: EntreeDossier[];
  memoire?: AvancementMemoire | null; // un projet seulement
  entreprise?: string | null; // son nom, qui nomme la racine socle/ à l'écran
};
export type TexteDossier = {
  chemin: string;
  texte: string;
  version: string;
  lecture_seule: boolean;
};

async function appel<T>(chemin: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API}${chemin}`, {
    cache: "no-store",
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    const corps = await r.json().catch(() => null);
    const e = corps?.error;
    if (r.status === 401 && e?.code === "session")
      window.dispatchEvent(new Event(SESSION));
    throw new ErreurApi(
      r.status,
      e?.code ?? "http",
      e?.message ?? r.statusText,
      e?.diagnostics ?? [],
    );
  }
  return r.json() as Promise<T>;
}

const json = (corps: unknown) => JSON.stringify(corps);

// Le client d'un espace de fichiers : le dossier d'un projet (`/api/affaires/<id>/dossier`) ou celui de l'organisation
// (`/api/organisation/dossier`), aux mêmes routes et aux mêmes formes (docs/DESK.md, contrat 1). Seule la base change.
export type Fichiers = {
  base: string;
  liste: () => Promise<Dossier>;
  url: (chemin: string) => string; // le fichier tel quel, pour un <iframe>, une <img>, un téléchargement
  lire: (chemin: string) => Promise<TexteDossier>;
  ecrire: (
    chemin: string,
    texte: string,
    version?: string,
  ) => Promise<{ chemin: string; version: string }>;
  lint: (
    chemin: string,
    texte: string,
  ) => Promise<{ diagnostics: Diagnostic[] }>;
  importer: (chemin: string, fichier: Blob) => Promise<EntreeDossier>;
  nouveauDossier: (chemin: string) => Promise<EntreeDossier>;
  nouveauFichier: (chemin: string, titre?: string) => Promise<EntreeDossier>;
  deplacer: (de: string, vers: string) => Promise<EntreeDossier>;
  jeter: (chemin: string) => Promise<{ chemin: string; corbeille: string }>;
  // Copie un fichier ou un dossier (de n'importe quelle racine) dans le dossier « vers », d'une racine en écriture.
  copier: (de: string, vers: string) => Promise<EntreeDossier>;
};

const clients = new Map<string, Fichiers>();
// Un client par base, toujours le même : il peut servir de dépendance à un effet.
export function fichiers(base: string): Fichiers {
  const connu = clients.get(base);
  if (connu) return connu;
  const q = (chemin: string) => `?chemin=${encodeURIComponent(chemin)}`;
  const f: Fichiers = {
    base,
    liste: () => appel<Dossier>(base),
    url: (chemin) => `${API}${base}/fichier${q(chemin)}`,
    lire: (chemin) => appel<TexteDossier>(`${base}/texte${q(chemin)}`),
    ecrire: (chemin, texte, version) =>
      appel(`${base}/texte`, {
        method: "PUT",
        body: json({ chemin, texte, version }),
      }),
    lint: (chemin, texte) =>
      appel(`${base}/lint`, { method: "POST", body: json({ chemin, texte }) }),
    importer: (chemin, fichier) =>
      appel<EntreeDossier>(`${base}/fichier${q(chemin)}`, {
        method: "PUT",
        body: fichier,
        headers: { "content-type": "application/octet-stream" },
      }),
    nouveauDossier: (chemin) =>
      appel<EntreeDossier>(`${base}/dossiers`, {
        method: "POST",
        body: json({ chemin }),
      }),
    nouveauFichier: (chemin, titre) =>
      appel<EntreeDossier>(`${base}/nouveau`, {
        method: "POST",
        body: json({ chemin, titre }),
      }),
    deplacer: (de, vers) =>
      appel<EntreeDossier>(`${base}/deplacer`, {
        method: "POST",
        body: json({ de, vers }),
      }),
    jeter: (chemin) =>
      appel(`${base}/fichier${q(chemin)}`, { method: "DELETE" }),
    copier: (de, vers) =>
      appel<EntreeDossier>(`${base}/copier`, {
        method: "POST",
        body: json({ de, vers }),
      }),
  };
  clients.set(base, f);
  return f;
}
export const fichiersAffaire = (id: string) =>
  fichiers(`/api/affaires/${id}/dossier`);
export const fichiersOrganisation = () => fichiers("/api/organisation/dossier");

export const api = {
  config: () => appel<Config>("/api/config"),
  affaires: () => appel<AffaireResume[]>("/api/affaires"),
  affaire: (id: string) => appel<Affaire>(`/api/affaires/${id}`),
  analyse: (id: string) => appel<Analyse>(`/api/affaires/${id}/analyse`),
  memoire: (id: string) => appel<Memoire>(`/api/affaires/${id}/memoire`),
  fils: (portee: Portee) => appel<Fil[]>(`${racine(portee)}/fils`),
  supprimerFil: (portee: Portee, fil: string) =>
    appel<{ supprime: boolean }>(`${racine(portee)}/fils/${fil}`, {
      method: "DELETE",
    }),
  texte: async (id: string, piece: string) => {
    const r = await fetch(`${API}/api/affaires/${id}/pieces/${piece}/texte`, {
      cache: "no-store",
    });
    if (!r.ok) throw new ErreurApi(r.status, "http", r.statusText);
    return r.text();
  },
  fichier: (id: string, piece: string) =>
    `${API}/api/affaires/${id}/pieces/${piece}/fichier`,
  ecrireSource: (
    id: string,
    document: Document,
    source: string,
    version: string,
  ) =>
    appel<{ version: string }>(`/api/affaires/${id}/${document}`, {
      method: "PUT",
      body: json({ source, version }),
    }),
  ecrireChapitre: (
    id: string,
    chapitre: string,
    bloc: string,
    version: string,
  ) =>
    appel<{ version: string }>(
      `/api/affaires/${id}/memoire/chapitres/${chapitre}`,
      {
        method: "PUT",
        body: json({ bloc, version }),
      },
    ),
  changerStatut: (
    id: string,
    chapitre: string,
    statut: Statut,
    version: string,
  ) =>
    appel<{ version: string }>(
      `/api/affaires/${id}/memoire/chapitres/${chapitre}/statut`,
      {
        method: "PUT",
        body: json({ statut, version }),
      },
    ),
  lint: (
    id: string,
    brouillon: {
      document: Document;
      source?: string;
      chapitre?: string;
      bloc?: string;
    },
  ) =>
    appel<{ ok: boolean; diagnostics: Diagnostic[] }>(
      `/api/affaires/${id}/lint`,
      {
        method: "POST",
        body: json(brouillon),
      },
    ),

  // Les arbitrages et l'avancement d'un projet.
  arbitrages: (id: string) =>
    appel<Arbitrages>(`/api/affaires/${id}/arbitrages`),
  trancher: (
    id: string,
    arbitrage: string,
    statut: StatutArbitrage,
    choix: ChoixArbitrage,
    version: string,
  ) =>
    appel<Arbitrages>(`/api/affaires/${id}/arbitrages/${arbitrage}`, {
      method: "PUT",
      body: json({ statut, ...choix, version }),
    }),
  taches: (id: string) => appel<Taches>(`/api/affaires/${id}/taches`),
  faire: (id: string, tache: string, statut: StatutTache, version?: string) =>
    appel<Taches>(`/api/affaires/${id}/taches/${tache}`, {
      method: "PUT",
      body: json({ statut, version }),
    }),
  avancement: (id: string) =>
    appel<Avancement>(`/api/affaires/${id}/avancement`),
  atteindre: (id: string, jalon: string, atteint: boolean, version: string) =>
    appel<Avancement>(`/api/affaires/${id}/avancement/jalons/${jalon}`, {
      method: "PUT",
      body: json({ atteint, version }),
    }),
  noter: (id: string, critere: string, note: number | null, version: string) =>
    appel<Avancement>(`/api/affaires/${id}/go-no-go/${critere}`, {
      method: "PUT",
      body: json({ note, version }),
    }),
  decider: (id: string, decision: Decision | null, version: string) =>
    appel<Avancement>(`/api/affaires/${id}/go-no-go/decision`, {
      method: "PUT",
      body: json({ decision, version }),
    }),
  conclure: (id: string, issue: Issue | null, version: string) =>
    appel<Avancement>(`/api/affaires/${id}/issue`, {
      method: "PUT",
      body: json({ issue, version }),
    }),

  // Le dossier d'un projet, par son identifiant (le client `fichiersAffaire(id)` le dit sans le répéter).
  dossier: (id: string) => fichiersAffaire(id).liste(),
  fichierDossier: (id: string, chemin: string) =>
    fichiersAffaire(id).url(chemin),
  lireDossier: (id: string, chemin: string) => fichiersAffaire(id).lire(chemin),
  ecrireDossier: (
    id: string,
    chemin: string,
    texte: string,
    version?: string,
  ) => fichiersAffaire(id).ecrire(chemin, texte, version),
  lintDossier: (id: string, chemin: string, texte: string) =>
    fichiersAffaire(id).lint(chemin, texte),
  importer: (id: string, chemin: string, fichier: Blob) =>
    fichiersAffaire(id).importer(chemin, fichier),
  nouveauDossier: (id: string, chemin: string) =>
    fichiersAffaire(id).nouveauDossier(chemin),
  nouveauFichier: (id: string, chemin: string, titre?: string) =>
    fichiersAffaire(id).nouveauFichier(chemin, titre),
  deplacer: (id: string, de: string, vers: string) =>
    fichiersAffaire(id).deplacer(de, vers),
  jeter: (id: string, chemin: string) => fichiersAffaire(id).jeter(chemin),
  copier: (id: string, de: string, vers: string) =>
    fichiersAffaire(id).copier(de, vers),

  // La base de l'entreprise.
  entreprise: () => appel<Entreprise>("/api/entreprise"),
  ecrireFiche: (fiche: string, bloc: string, version: string) =>
    appel<{ version: string }>(`/api/entreprise/fiches/${fiche}`, {
      method: "PUT",
      body: json({ bloc, version }),
    }),
  creerFiche: (rubrique: string, bloc: string, version: string) =>
    appel<{ id: string; version: string }>(
      `/api/entreprise/rubriques/${rubrique}/fiches`,
      {
        method: "POST",
        body: json({ bloc, version }),
      },
    ),
  supprimerFiche: (fiche: string, version: string) =>
    appel<{ version: string }>(
      `/api/entreprise/fiches/${fiche}?version=${encodeURIComponent(version)}`,
      {
        method: "DELETE",
      },
    ),
  lintEntreprise: (brouillon: {
    fiche?: string;
    rubrique?: string;
    bloc: string;
  }) =>
    appel<{ ok: boolean; diagnostics: Diagnostic[] }>("/api/entreprise/lint", {
      method: "POST",
      body: json(brouillon),
    }),

  // La Marketplace : des appels d'offres publiés (saisis à la main ou sourcés au BOAMP et à TED), qu'on prend pour en
  // faire un projet. La recherche filtre par lieu, texte, catégorie (app/sourcing/routes.py).
  marketplace: () =>
    appel<Recherche>("/api/marketplace/recherche?limit=500").then(
      (r) => r.annonces,
    ),
  recherche: (filtres: Record<string, string | number | boolean>) =>
    appel<Recherche>(
      `/api/marketplace/recherche?${new URLSearchParams(
        Object.entries(filtres)
          .filter(([, v]) => v !== "" && v !== false && v !== 0)
          .map(([k, v]) => [k, String(v)]),
      )}`,
    ),
  // Des annonces par identifiant : la fiche de l'appel d'offres de chaque projet.
  annoncesParId: (ids: string[]) =>
    appel<{ annonces: Annonce[]; libelles: Libelles }>(
      `/api/marketplace/annonces?ids=${encodeURIComponent(ids.join(","))}`,
    ),
  // La requête enregistrée de l'entreprise, et le match que Jev en fait (app/sourcing/match.py).
  requete: () => appel<RequeteEntreprise>("/api/marketplace/requete"),
  enregistrerRequete: (requete: {
    criteres: Record<string, string | number | boolean>;
    seuils: Seuils;
    auto: boolean;
  }) =>
    appel<RequeteEntreprise>("/api/marketplace/requete", {
      method: "PUT",
      body: json(requete),
    }),
  lancerMatch: () =>
    appel<{ lance: boolean }>("/api/marketplace/requete/match", {
      method: "POST",
    }),
  sourcing: () => appel<AdminSourcing>("/api/admin/sourcing"),
  reglerSourcing: (changements: Partial<ReglagesSourcing>) =>
    appel<ReglagesSourcing>("/api/admin/sourcing/reglages", {
      method: "PUT",
      body: json(changements),
    }),
  lancerPasse: () =>
    appel<{ lancee: boolean }>("/api/admin/sourcing/passe", {
      method: "POST",
    }),
  annoncesSourcees: (filtres: Record<string, string | number>) =>
    appel<{ total: number; offset: number; annonces: AnnonceSourcee[] }>(
      `/api/admin/sourcing/annonces?${new URLSearchParams(
        Object.entries(filtres)
          .filter(([, v]) => v !== "")
          .map(([k, v]) => [k, String(v)]),
      )}`,
    ),
  poserAdresse: (id: string, q: string) =>
    appel<Adresse>(`/api/admin/sourcing/annonces/${id}/adresse`, {
      method: "POST",
      body: json({ q }),
    }),
  retirerAdresse: (id: string) =>
    appel<{ retiree: boolean; adresse: Adresse | null }>(
      `/api/admin/sourcing/annonces/${id}/adresse`,
      {
        method: "DELETE",
      },
    ),
  resituer: (id: string) =>
    appel<AnnonceSourcee>(`/api/admin/sourcing/annonces/${id}/resituer`, {
      method: "POST",
    }),
  prendre: (id: string) =>
    appel<{ affaire: string; pris: boolean }>(
      `/api/marketplace/${id}/prendre`,
      { method: "POST" },
    ),

  // L'orchestrateur.
  objectif: () => appel<EtatObjectif[]>("/api/objectif"),
  relancer: (affaire?: string) =>
    appel<{ relances: string[] }>("/api/objectif/relancer", {
      method: "POST",
      body: json({ affaire }),
    }),
  pauseProjet: (affaire: string, pause: boolean) =>
    appel<EtatObjectif>(`/api/objectif/${affaire}/pause`, {
      method: "PUT",
      body: json({ pause }),
    }),
  agentDuProjet: (affaire: string) =>
    appel<AgentDuProjet>(`/api/objectif/${affaire}/agent`),
  orchestrateur: () => appel<Orchestrateur>("/api/orchestrateur"),
  reveiller: () =>
    appel<Orchestrateur>("/api/orchestrateur/reveiller", { method: "POST" }),
  activer: (actif: boolean) =>
    appel<Orchestrateur>("/api/orchestrateur", {
      method: "PUT",
      body: json({ actif }),
    }),
  // La Veille.
  veille: () => appel<Veille>("/api/veille"),
  // L'Intendant.
  intendant: () => appel<Intendant>("/api/intendant"),
};

export type Annonce = {
  id: string;
  objet: string;
  reference: string;
  acheteur: string;
  lieu: string;
  procedure: string;
  categorie: string;
  cpv: string;
  date_limite: string; // "YYYY-MM-DD HH:MM"
  lots?: number;
  resume: string;
  source: string; // la consultation sur sa plateforme (PLACE, AWS, achatpublic…)
  pris: boolean;
  dce: { octets: number; fichiers: number; retirable: boolean; pret?: boolean };
  // Le sourcing (app/sourcing) : d'où vient l'annonce et où est le chantier.
  origine?: "manuel" | "boamp" | "ted";
  avis?: string; // l'avis au BOAMP ou à TED
  publie?: string;
  plateforme?: string; // l'hôte de la plateforme de dématérialisation
  editeur?: string;
  famille?: string;
  departements?: string[];
  adresse?: Adresse | null;
  distance_km?: number | null;
  rectificatifs?: { idweb: string; publie: string; avis: string }[];
  // Ce que l'avis dit du chantier (app/sourcing/fiche.py), ce que Jev en lit (lecture.py), et le match.
  fiche?: FicheAvis;
  lecture?: Lecture;
  montant?: Montant | null;
  match?: Match | null;
};

export type FicheAvis = {
  description: string;
  lots: { numero: string; intitule: string; montant?: number | null }[];
  montant: { valeur?: number; basse?: number; haute?: number } | null;
  duree_mois: number | null;
  debut: string;
  visite: boolean | null;
  variantes: boolean | null;
  accord_cadre: boolean;
  cpv: string[];
};
export type NiveauLu = { niveau: number; score: number; confiance: number };
export type Lecture = {
  tags: string[];
  principal: { choix: string; confiance: number };
  nature: { choix: string; confiance: number };
  ouvrage: { choix: string; confiance: number };
  complexite: NiveauLu;
  tranche: NiveauLu & { cle: string };
  marques: string[];
  modele: string;
  lu_le: string;
};
// Le montant en fourchette : celui de l'avis, sinon la tranche que Jev estime.
export type Montant = {
  bas: number;
  haut: number | null;
  estime: boolean;
  tranche?: string;
};
export type MatchLot = {
  score: number; // de 0 à 100
  niveau: number;
  confiance: number;
  raisons: Record<string, number>;
};
// Une annonce allotie se matche lot par lot : son match est celui de son meilleur lot (`lot`).
export type Match = MatchLot & {
  le: string;
  lot?: { numero: string; intitule: string };
  lots?: (MatchLot & { numero: string; intitule: string })[];
};
export type Seuils = { bas: number; haut: number };
export type Libelles = {
  corps: Record<string, string>;
  natures: Record<string, string>;
  ouvrages: Record<string, string>;
  traits: Record<string, string>;
  complexites: string[];
  tranches: Record<
    string,
    { libelle: string; bas: number; haut: number | null }
  >;
};
export type Point = {
  id: string;
  objet: string;
  acheteur: string;
  date_limite: string;
  url?: string;
  origine?: string;
  lat: number;
  lon: number;
  precision: Precision;
  ville?: string;
  tags: string[];
  montant: Montant | null;
  lots: number;
  match: number | null;
  fit?: Match | null; // le match entier, pour la jauge de la bulle
};
export type RequeteEntreprise = {
  entreprise: string;
  nom: string;
  criteres: Record<string, string | number | boolean>;
  seuils: Seuils;
  auto: boolean;
  maj: string | null;
  enregistree: boolean;
  dernier_run: {
    le: string;
    declencheur: string;
    selection: number;
    matchees: number;
    cout: number;
    secondes: number;
    erreurs: string[];
  } | null;
  en_cours: { total: number; faits: number; debut: number } | null;
  matchs: number;
  niveaux: string[];
  raisons: Record<string, string>;
};

// L'adresse d'un chantier, à la précision qu'on a pu trouver (le géocodage de la Base adresse nationale).
export type Precision =
  "adresse" | "rue" | "ville" | "departement" | "inconnue";
export const LIBELLES_PRECISION: Record<Precision, string> = {
  adresse: "à l'adresse",
  rue: "à la rue",
  ville: "à la ville",
  departement: "au département",
  inconnue: "lieu inconnu",
};
export type Adresse = {
  texte: string;
  ville?: string;
  cp?: string;
  insee?: string;
  dep?: string;
  lat?: number;
  lon?: number;
  precision: Precision;
  origine?: string; // avis, objet, acheteur, llm, admin
  point?: "chantier" | "acheteur";
};

// L'entreprise sur la carte : l'adresse de sa fiche identité, géocodée.
export type Siege = {
  lat: number;
  lon: number;
  label: string;
  nom: string | null;
  adresse: string;
};

export type Recherche = {
  total: number;
  offset: number;
  annonces: Annonce[];
  centre: { lat: number; lon: number; label: string } | null;
  points: Point[] | null; // avec `vue=carte`
  siege?: Siege | null; // l'entreprise, avec `vue=carte`
  non_situees: number;
  lues: number;
  matchs: number;
  libelles: Libelles;
  lieux: {
    departements: Record<string, string>;
    regions: Record<string, { nom: string; departements: string[] }>;
  };
  facettes: {
    departements: Record<string, number>;
    categories: Record<string, number>;
    origines: Record<string, number>;
    precisions: Record<string, number>;
    corps: Record<string, number>;
    natures: Record<string, number>;
    ouvrages: Record<string, number>;
    traits: Record<string, number>;
    complexites: Record<string, number>;
    tranches: Record<string, number>;
    accords_cadres: number;
    visites: number;
    scores: number[];
  };
};

// L'admin du sourcing (`/admin/sourcing`).
export type ReglagesSourcing = {
  veille: boolean;
  frequence_heures: number;
  sources: Record<string, boolean>;
  categories: string[];
  regions: string[];
  departements: string[];
  geocodage: boolean;
  llm: {
    actif: boolean;
    modele: string;
    budget_passe: number;
    max_avis: number;
  };
};
export type EtapePasse = {
  requetes: number;
  avis: number;
  octets?: number;
  erreurs?: string[];
  nouveaux?: number;
  mis_a_jour?: number;
  doublons?: number;
  retires?: number;
  hors_zone?: number;
  cache?: number;
  jetons_entree?: number;
  jetons_sortie?: number;
  cout?: number;
  secondes?: number;
};
export type PasseSourcing = {
  id: string;
  declencheur: string;
  debut: number;
  fin: number;
  duree: number | null;
  total: number;
  cout: number;
  etapes: Record<string, EtapePasse>;
};
export type Plateforme = {
  hote: string;
  editeur: string;
  editeur_libelle: string;
  avis: number;
  travaux: number;
  part: number;
  cumul: number;
  dce: boolean;
  sources: Record<string, number>;
};
export type AdminSourcing = {
  reglages: ReglagesSourcing;
  en_cours: { declencheur: string; debut: number; etape: string } | null;
  sources: {
    id: string;
    nom: string;
    genre: string;
    cout: string;
    url: string;
    couvre: string;
  }[];
  passes: PasseSourcing[];
  totaux: {
    annonces: number;
    origines: Record<string, number>;
    categories: Record<string, number>;
    precisions: Partial<Record<Precision, number>>;
    editeurs: Record<string, number>;
    retirables: number;
    relues_llm: number;
    corrigees: number;
  };
  manuelles: number;
  couts: {
    derniere: number;
    trente_jours: number;
    requetes_trente_jours: number;
    octets_trente_jours: number;
  };
  registre: Plateforme[];
  plateformes: number;
  instances_atexo: number;
  geocodage: { cache: number };
  llm: { cle: boolean };
  departements: Record<string, string>;
  regions: Record<string, { nom: string; departements: string[] }>;
};
export type AnnonceSourcee = Annonce & {
  corrigee?: boolean;
  pistes?: { chantier: { q: string; type: string; origine: string }[] };
};

// Les nouvelles de la base, en direct (`GET /api/evenements`, app/evenements.py).
export type Evenement =
  | { type: "document"; genre: string; id: string }
  | { type: "agent"; fil: string; affaire: string | null; actif: boolean }
  | { type: "affaire"; affaire: string; action: "prise" }
  | { type: "decision"; affaire: string }
  | { type: "fil"; fil: string; affaire: string | null }
  | { type: "orchestrateur" }
  | { type: "sourcing"; action: "passe" | "echec" | "match"; total?: number }
  | { type: "dossier"; portee: "organisation" }; // un fichier de l'espace de l'organisation a changé
export const EVENEMENTS = `${API}/api/evenements`;

// Le tour d'un agent en direct, hors du chat (`GET /api/fils/{fil}/direct`, app/en_direct.py) : un instantané à chaque
// changement, puis `actif: false` quand il finit.
export type EtapeDirecte = {
  emoji: string;
  libelle: string;
  contenu: string;
  statut: string; // « en cours », « faite », ou « » : le plan
};
export type Direct =
  | { fil: string; actif: true; texte: string; etapes: EtapeDirecte[] }
  | { fil: string; actif: false };
export const enDirect = (fil: string) =>
  `${API}/api/fils/${encodeURIComponent(fil)}/direct`;

// Le préfixe des routes d'une portée : un projet garde ses chemins /api/affaires.
const racine = (portee: Portee) =>
  portee.genre === "projet" ? `/api/affaires/${portee.id}` : "/api/entreprise";
